/**
 * @file JsonLdViewer.tsx
 * @description Modal component for viewing and copying JSON-LD structured data.
 */

import React, { useState, useRef } from "react";
import { toast } from "react-toastify";

interface JsonLdViewerProps {
  jsonLd: string;
  isOpen: boolean;
  onClose: () => void;
}

const JsonLdViewer: React.FC<JsonLdViewerProps> = ({
  jsonLd,
  isOpen,
  onClose,
}) => {
  const [isCopied, setIsCopied] = useState(false);
  const dialogRef = useRef<HTMLDialogElement>(null);

  React.useEffect(() => {
    if (isOpen && dialogRef.current) {
      dialogRef.current.showModal();
    } else if (!isOpen && dialogRef.current) {
      dialogRef.current.close();
    }
  }, [isOpen]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(jsonLd);
      setIsCopied(true);
      toast.success("JSON-LD copiato negli appunti");
      setTimeout(() => setIsCopied(false), 2000);
    } catch (err) {
      toast.error("Impossibile copiare il JSON-LD");
    }
  };

  // Format JSON with proper indentation
  let formattedJson = jsonLd;
  try {
    const parsed = JSON.parse(jsonLd);
    formattedJson = JSON.stringify(parsed, null, 2);
  } catch {
    // If parsing fails, use original string
  }

  if (!jsonLd) {
    return null;
  }

  return (
    <dialog
      ref={dialogRef}
      className={`modal ${isOpen ? "modal-open" : ""}`}
      onClose={onClose}
    >
      <div className="modal-box max-w-3xl bg-base-100">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-2xl font-bold normal-case text-neutral">
            Dati strutturati Schema.org
          </h3>
          <button
            onClick={onClose}
            className="btn btn-sm btn-circle btn-ghost"
            aria-label="Chiudi"
          >
            ✕
          </button>
        </div>

        <div className="mb-4">
          <p className="text-sm text-base-content/70">
            JSON-LD che descrive l'entità principale dell'articolo per i motori
            di ricerca.
          </p>
        </div>

        <div className="relative">
          <pre className="p-4 mb-4 overflow-auto text-sm bg-base-200 rounded-lg max-h-96">
            <code className="text-base-content">{formattedJson}</code>
          </pre>
          <button
            onClick={handleCopy}
            className={`px-4 py-2 text-sm rounded-full btn ${
              isCopied
                ? "btn-success"
                : "btn-outline btn-primary"
            }`}
          >
            {isCopied ? "✓ Copiato" : "Copia JSON"}
          </button>
        </div>

        <div className="modal-action">
          <button
            onClick={onClose}
            className="px-6 text-base rounded-full btn btn-outline btn-primary"
          >
            Chiudi
          </button>
        </div>
      </div>
      <form method="dialog" className="modal-backdrop" onSubmit={onClose}>
        <button onClick={onClose}>close</button>
      </form>
    </dialog>
  );
};

export default JsonLdViewer;
